/**
 * Seo — per-route document head management.
 *
 * Why imperative upsert instead of React 19's native <title>/<meta>
 * hoisting: the static index.html already ships default title/description/
 * OG tags, and the Vercel render function (api/render.js) overrides them
 * server-side for crawlers. React 19 would *append* its own copies rather
 * than replace those, leaving duplicate <title>/<meta> in the head. Upserting
 * by selector guarantees exactly one of each tag, whoever set it first.
 *
 * Division of labour:
 *   - api/render.js  → correct head in the *initial* HTML (non-JS crawlers,
 *     social link-preview bots, first paint).
 *   - this component → keeps the head correct across client-side SPA
 *     navigations (tab title, share-from-app, JS-running crawlers).
 */

import { useEffect } from 'react';

export const SITE_URL = 'https://katha-mithra.moderndaykarmayogi.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const JSONLD_ID = 'seo-page-jsonld';

const upsertMeta = (attr, key, content) => {
  if (content == null || content === '') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (obj) => {
  const existing = document.getElementById(JSONLD_ID);
  if (!obj) {
    if (existing) existing.remove();
    return;
  }
  const script = existing || document.createElement('script');
  script.id = JSONLD_ID;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(obj);
  if (!existing) document.head.appendChild(script);
};

const Seo = ({
  title,
  description,
  path,
  image,
  type = 'website',
  noindex = false,
  jsonLd = null,
}) => {
  // jsonLd is an object literal recreated each render; stringify so the
  // effect only re-runs on actual content change, not identity change.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    const url = path ? `${SITE_URL}${path}` : undefined;
    const img = image || DEFAULT_IMAGE;

    if (title) document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    if (url) upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', img);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', img);

    if (url) upsertLink('canonical', url);
    setJsonLd(jsonLdKey ? JSON.parse(jsonLdKey) : null);
  }, [title, description, path, image, type, noindex, jsonLdKey]);

  return null;
};

export default Seo;
